import { useState, useCallback, useMemo } from "react";
import { Calendar, Building2, Megaphone } from "lucide-react";
import { Button } from "reactstrap";

import { GroupApprovalModal } from "./GroupApprovalModal";
import { formatGridCurrency } from "../utils/formatters";

interface BaseGroupHeaderProps {
  data: any;
}

interface GroupHeaderWithModalProps {
  data: any;
  groupType: "dateGroup" | "brandGroup" | "campaignGroup";
  milestones: any[];
  parentBrand?: string;
  parentDate?: string;
}

/**
 * Date Group Header - Top level grouping by date
 * Handles date bucket formats: "YYYY-MM-01", "YYYY-MM-15", or "unknown"
 */
export const DateGroupHeaderRenderer = ({
  data,
  milestones,
}: GroupHeaderWithModalProps) => {
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [unapproveModalOpen, setUnapproveModalOpen] = useState(false);

  // Format the date display based on the bucket type - memoized for performance
  const formattedDate = useMemo(() => {
    const dateStr = data.date;
    if (dateStr === "unknown") {
      return "Unknown Date";
    }

    // Handle YYYY-MM-01 and YYYY-MM-15 formats
    if (dateStr.endsWith("-01")) {
      const [year, month] = dateStr.split("-");
      return `${year}-${month} (1st-14th)`;
    } else if (dateStr.endsWith("-15")) {
      const [year, month] = dateStr.split("-");
      return `${year}-${month} (15th-End)`;
    }

    return dateStr; // fallback
  }, [data.date]);

  const handleApproveAll = useCallback(() => {
    setApproveModalOpen(true);
  }, []);

  const handleUnapproveAll = useCallback(() => {
    setUnapproveModalOpen(true);
  }, []);

  const handleCloseApproveModal = useCallback(() => {
    setApproveModalOpen(false);
  }, []);

  const handleCloseUnapproveModal = useCallback(() => {
    setUnapproveModalOpen(false);
  }, []);

  return (
    <>
      <div className="approvals-group-header approvals-group-header--date d-flex align-items-center justify-content-between px-3 py-2">
        <div className="d-flex align-items-center gap-2">
          <Calendar size={18} />
          <strong>{formattedDate}</strong>
        </div>
        <div className="d-flex align-items-center gap-3">
          <span className="fw-bold">
            {formatGridCurrency(data.unapprovedAmount)}
          </span>
          <div className="d-flex gap-2">
            <Button
              color="success"
              size="sm"
              outline
              onClick={handleApproveAll}
              disabled={data.unapprovedCount === 0}
            >
              Approve All ({data.unapprovedCount})
            </Button>
            <Button
              color="danger"
              size="sm"
              outline
              onClick={handleUnapproveAll}
              disabled={data.approvedCount === 0}
            >
              Unapprove All ({data.approvedCount})
            </Button>
          </div>
        </div>
      </div>

      <GroupApprovalModal
        isOpen={approveModalOpen}
        onClose={handleCloseApproveModal}
        milestones={milestones}
        groupType="dateGroup"
        groupName={formattedDate}
        actionType="approve"
      />

      <GroupApprovalModal
        isOpen={unapproveModalOpen}
        onClose={handleCloseUnapproveModal}
        milestones={milestones}
        groupType="dateGroup"
        groupName={formattedDate}
        actionType="unapprove"
      />
    </>
  );
};

/**
 * Brand Group Header - Second level grouping by brand
 */
export const BrandGroupHeaderRenderer = ({
  data,
  milestones,
  parentDate,
}: GroupHeaderWithModalProps) => {
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [unapproveModalOpen, setUnapproveModalOpen] = useState(false);

  const handleApproveAll = useCallback(() => {
    setApproveModalOpen(true);
  }, []);

  const handleUnapproveAll = useCallback(() => {
    setUnapproveModalOpen(true);
  }, []);

  const handleCloseApproveModal = useCallback(() => {
    setApproveModalOpen(false);
  }, []);

  const handleCloseUnapproveModal = useCallback(() => {
    setUnapproveModalOpen(false);
  }, []);

  return (
    <>
      <div className="approvals-group-header approvals-group-header--brand d-flex align-items-center justify-content-between px-3 py-2">
        <div className="d-flex align-items-center gap-2">
          <Building2 size={16} />
          <strong>{data.brandName}</strong>
        </div>
        <div className="d-flex align-items-center gap-3">
          <span className="fw-bold">
            {formatGridCurrency(data.unapprovedAmount)}
          </span>
          <div className="d-flex gap-2">
            <Button
              color="success"
              size="sm"
              outline
              onClick={handleApproveAll}
              disabled={data.unapprovedCount === 0}
            >
              Approve All ({data.unapprovedCount})
            </Button>
            <Button
              color="danger"
              size="sm"
              outline
              onClick={handleUnapproveAll}
              disabled={data.approvedCount === 0}
            >
              Unapprove All ({data.approvedCount})
            </Button>
          </div>
        </div>
      </div>

      <GroupApprovalModal
        isOpen={approveModalOpen}
        onClose={handleCloseApproveModal}
        milestones={milestones}
        groupType="brandGroup"
        groupName={data.brandName}
        parentDate={parentDate}
        actionType="approve"
      />

      <GroupApprovalModal
        isOpen={unapproveModalOpen}
        onClose={handleCloseUnapproveModal}
        milestones={milestones}
        groupType="brandGroup"
        groupName={data.brandName}
        parentDate={parentDate}
        actionType="unapprove"
      />
    </>
  );
};

/**
 * Campaign Group Header - Third level grouping by campaign
 */
export const CampaignGroupHeaderRenderer = ({
  data,
  milestones,
  parentBrand,
  parentDate,
}: GroupHeaderWithModalProps) => {
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [unapproveModalOpen, setUnapproveModalOpen] = useState(false);

  const handleApproveAll = useCallback(() => {
    setApproveModalOpen(true);
  }, []);

  const handleUnapproveAll = useCallback(() => {
    setUnapproveModalOpen(true);
  }, []);

  const handleCloseApproveModal = useCallback(() => {
    setApproveModalOpen(false);
  }, []);

  const handleCloseUnapproveModal = useCallback(() => {
    setUnapproveModalOpen(false);
  }, []);

  return (
    <>
      <div className="approvals-group-header approvals-group-header--campaign d-flex align-items-center justify-content-between px-3 py-2">
        <div className="d-flex align-items-center gap-2">
          <Megaphone size={14} />
          <strong>{data.campaignName}</strong>
        </div>
        <div className="d-flex align-items-center gap-3">
          <span className="fw-bold">
            {formatGridCurrency(data.unapprovedAmount)}
          </span>
          <div className="d-flex gap-2">
            <Button
              color="success"
              size="sm"
              outline
              onClick={handleApproveAll}
              disabled={data.unapprovedCount === 0}
            >
              Approve All ({data.unapprovedCount})
            </Button>
            <Button
              color="danger"
              size="sm"
              outline
              onClick={handleUnapproveAll}
              disabled={data.approvedCount === 0}
            >
              Unapprove All ({data.approvedCount})
            </Button>
          </div>
        </div>
      </div>

      <GroupApprovalModal
        isOpen={approveModalOpen}
        onClose={handleCloseApproveModal}
        milestones={milestones}
        groupType="campaignGroup"
        groupName={data.campaignName}
        parentBrand={parentBrand}
        parentDate={parentDate}
        actionType="approve"
      />

      <GroupApprovalModal
        isOpen={unapproveModalOpen}
        onClose={handleCloseUnapproveModal}
        milestones={milestones}
        groupType="campaignGroup"
        groupName={data.campaignName}
        parentBrand={parentBrand}
        parentDate={parentDate}
        actionType="unapprove"
      />
    </>
  );
};

/**
 * Factory function to create the appropriate group header renderer
 */
export const createGroupHeaderRenderer = () => {
  return (params: any) => {
    const { data } = params;

    if (!data) {
      return <div>No data</div>;
    }

    // Use the items array directly from the group data (already provided by backend)
    const milestones = data.items || [];

    switch (data.rowType) {
      case "dateGroup":
        return (
          <DateGroupHeaderRenderer
            data={data}
            milestones={milestones}
            groupType="dateGroup"
          />
        );
      case "brandGroup":
        return (
          <BrandGroupHeaderRenderer
            data={data}
            milestones={milestones}
            groupType="brandGroup"
            parentDate={data.parentDate}
          />
        );
      case "campaignGroup":
        return (
          <CampaignGroupHeaderRenderer
            data={data}
            milestones={milestones}
            groupType="campaignGroup"
            parentBrand={data.parentBrand}
            parentDate={data.parentDate}
          />
        );
      default:
        return <div>Unknown group type: {data.rowType}</div>;
    }
  };
};
